import React from 'react';
import { useQuery } from '@tanstack/react-query';
import apiService from '../../services/shared/apiService';

interface WeeklyInsightsProps {
  symbolicTags: any;
  userId: string;
}

const WeeklyInsights: React.FC<WeeklyInsightsProps> = ({ symbolicTags, userId }) => {
  // Fetch AI-generated insights for the past week
  const { data: insights, isLoading: insightsLoading } = useQuery({
    queryKey: ['weekly-insights', userId],
    queryFn: () => apiService.getMySymbolicTrends(),
    enabled: !!userId,
  });

  // Process symbolic tags to get top tags
  const getTopTags = () => {
    if (!symbolicTags || !symbolicTags.tags) return [];
    
    const tagCounts: Record<string, number> = {};
    symbolicTags.tags.forEach((tag: string) => {
      tagCounts[tag] = (tagCounts[tag] || 0) + 1;
    });

    return Object.entries(tagCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, 3)
      .map(([tag, count]) => ({ tag, count }));
  };

  const getTagEmoji = (tag: string) => {
    const emojiMap: Record<string, string> = {
      joy: '😊', growth: '🌱', confidence: '💪', energy: '⚡', clarity: '✨',
      gratitude: '🙏', peace: '🕊️', strength: '💪', resilience: '🛡️', healing: '🩹',
      anxiety: '😰', sadness: '😢', anger: '😠', fatigue: '😴', confusion: '😕',
      overwhelm: '😵', isolation: '🏝️', shame: '😔', hopelessness: '😞', inertia: '🛑',
      reflection: '🤔', uncertainty: '❓', questioning: '🤷', rest: '😌', sleep: '😴',
      focus: '🎯', scatter: '💫', connection: '🤝', lonely: '🥺'
    };
    return emojiMap[tag] || '📝';
  };

  const getTagColor = (tag: string) => {
    const positiveTags = ['joy', 'growth', 'confidence', 'energy', 'clarity', 'gratitude', 'peace', 'strength', 'resilience', 'healing'];
    const negativeTags = ['anxiety', 'sadness', 'anger', 'fatigue', 'confusion', 'overwhelm', 'isolation', 'shame', 'hopelessness', 'inertia'];
    const neutralTags = ['reflection', 'uncertainty', 'questioning', 'rest', 'sleep', 'focus', 'scatter', 'connection', 'lonely'];

    if (positiveTags.includes(tag)) return 'bg-green-100 text-green-800';
    if (negativeTags.includes(tag)) return 'bg-red-100 text-red-800';
    if (neutralTags.includes(tag)) return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  const topTags = getTopTags();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* AI-Generated Insights */}
      <div className="bg-white  p-6 rounded-lg shadow-md">
        <div className="flex items-center mb-4">
          <div className="text-2xl mr-3">💡</div>
          <h3 className="text-lg font-semibold">AI Insights</h3>
        </div>
        
        {insightsLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
          </div>
        ) : insights && insights.insights ? (
          <div className="space-y-4">
            {insights.insights.slice(0, 3).map((insight: any, index: number) => (
              <div key={index} className="bg-gray-50  p-4 rounded-lg">
                <div className="flex items-start space-x-2">
                  <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
                  <p className="text-sm text-gray-700 ">
                    {insight.text}
                  </p>
                </div>
                {insight.confidence && (
                  <div className="mt-2 text-xs text-gray-500 ">
                    Confidence: {Math.round(insight.confidence * 100)}%
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-3xl mb-3">🤖</div>
            <p className="text-gray-500 ">
              AI insights will appear here as you continue chatting.
            </p>
          </div>
        )}
      </div>

      {/* Top Symbolic Tags */}
      <div className="bg-white  p-6 rounded-lg shadow-md">
        <div className="flex items-center mb-4">
          <div className="text-2xl mr-3">🏷️</div>
          <h3 className="text-lg font-semibold">Top Tags This Week</h3>
        </div>
        
        {topTags.length > 0 ? (
          <div className="space-y-4">
            {topTags.map(({ tag, count }, index) => (
              <div key={tag} className="flex items-center justify-between p-3 bg-gray-50  rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="text-lg">{getTagEmoji(tag)}</div>
                  <div>
                    <div className="font-medium capitalize">{tag}</div>
                    <div className="text-sm text-gray-500 ">
                      {count} occurrence{count !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-medium ${getTagColor(tag)}`}>
                  #{index + 1}
                </div>
              </div>
            ))}
            
            <div className="mt-4 p-3 bg-blue-50  rounded-lg">
              <div className="text-sm text-blue-800 ">
                <strong>Pattern Analysis:</strong> Your most frequent emotional states this week show 
                {topTags[0]?.tag && ` a focus on "${topTags[0].tag}"`}. This can help identify 
                recurring themes in your mental health journey.
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="text-3xl mb-3">📊</div>
            <p className="text-gray-500 ">
              Start chatting to see your top emotional patterns.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default WeeklyInsights; 